import React from "react";
import styled from "styled-components";

import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { ILayoutTypeMap } from "@formant/core/model/ILayoutTypeMap";
import { IModule } from "@formant/core/model/IModule";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";
import { IconName } from "@formant/ui/component/common/Icon";

import { track } from "../../analytics";
import { useStores } from "../../context/StoreContext";
import { analyticsStore, observeStore, viewStore } from "../../stores";
import PickerTabGroup, { IPickerItem } from "../common/PickerTabGroup";
import { Popup } from "../common/Popup";
import { isValidTaskReport } from "../StreamPicker/NewAnalyticsPicker";
import {
    createAnalyticsModule,
    taskReportToModule,
} from "./TaskReportsTemplates";

interface ICreateViewPopup {
    views: IView[];
    modules: IModule[];
    tags: ITags;
    deviceIds?: Uuid[];
    onViewCreated: (viewId: Uuid) => void;
    onCancel: () => void;
}

export function CreateViewPopup({
    onViewCreated,
    onCancel,
    views,
    tags,
    deviceIds,
}: ICreateViewPopup) {
    const [selectedTab, setSelectedTab] = React.useState<string>("library");

    const { authStore } = useStores();

    const navigatorEnabled = true;

    const duplicateView = (view: IView) => {
        const {
            name,
            tags: ogTags,
            layout,
            layoutType,
            filter,
            configuration,
        } = view;
        const newView = freshView(`${name} copy`, layoutType || "observe");
        return {
            ...newView,
            ...{
                name,
                tags: ogTags,
                layout,
                layoutType,
                filter,
                configuration,
            },
        };
    };

    const freshView = (name: string, layoutType: LayoutType): IView => {
        const emptyFilter = { names: [] };

        const fullFilter = {
            deviceIds: deviceIds || [],
        };

        const groupFilter =
            (views.every(_ => _.groupFilter) &&
                views.find(_ => _.groupFilter)?.groupFilter) ||
            null;
        const groupId =
            (views.every(_ => _.smartFleetId) &&
                views.find(_ => _.smartFleetId)?.smartFleetId) ||
            null;

        return {
            name,
            layout: {},
            filter: layoutType === "teleop" ? emptyFilter : fullFilter,
            groupFilter,
            tags: layoutType === "teleop" ? {} : tags,
            layoutType,
            index: 0,
            configuration: [],
            deviceFilter: {},
            smartFleetId: groupFilter ? null : groupId,
        };
    };

    const createView = async (
        name: string,
        layoutType: LayoutType,
        dupe?: IView,
    ): Promise<IView | undefined> => {
        let view: IView;
        if (dupe) {
            view = duplicateView(dupe) as IView;
        } else {
            view = freshView(name, layoutType);
        }

        // if we already have a number at the end of the name, increment it
        let existingNumber: number | undefined;
        const numericEnding = view.name.match(/\s([0-9]+)$/);
        const baseName = view.name.replace(/\s([0-9]+)$/, "");

        if (numericEnding) {
            existingNumber = Number(numericEnding[0].trim());
            view.name = baseName;
        }
        const viewList = await viewStore.list({ async: true, force: true });

        let i = existingNumber || 1;
        while (true) {
            const uniqueName = `${view.name} ${i}`;
            if (!viewList.find(_ => _.name === uniqueName)) {
                view.name = uniqueName;
                break;
            }
            i++;
        }

        if (authStore.hasAccessByRole("administrator")) {
            const createdView = await viewStore.create(view);
            observeStore.isCustomizing = true;
            observeStore.configuringAnalyticsId = undefined;
            onViewCreated(createdView.id);
            return createdView;
        }
    };

    const createObserveView = async () => {
        track("Create Observe View");
        await createView("Observe View", "observe");
    };
    const createAnalyticsView = async () => {
        track("Create Analytics View");
        await createView("Analytics View", "analytics");
    };
    const createAnalyticsTemplateView = async (
        name: string,
    ): Promise<IView | undefined> => {
        return await createView(name, "analytics");
    };
    const createNavigatorView = async () => {
        track("Create Navigator View");
        await createView("Navigator View", "teleop");
    };

    const makeItem = (
        name: string,
        icon: IconName,
        onToggle: (label: string) => void,
    ) => ({
        key: name,
        label: name,
        icon,
        disabled: false,
        checked: false,
        toggled: true,
        onToggle,
    });

    const layoutToIconsMap: Record<keyof ILayoutTypeMap, IconName> = {
        dashboard: "newView", // "dashboard",
        observe: "newView",
        teleop: "joystick",
        analytics: "numeric",
        fullscreen: "cube",
    };

    const taskReportTemplates = (
        analyticsStore.listTaskReports({ deviceIds: deviceIds || [] }) || []
    )
        .filter(_ => isValidTaskReport(_))
        .map(_ =>
            makeItem(`Dashboard ${_.tableName}`, "event", async () => {
                const view = await createAnalyticsTemplateView(
                    `Task Report Dashboard ${_.tableName}`,
                );
                if (view && view.layout) {
                    await createAnalyticsModule(view, [
                        taskReportToModule(_, view),
                    ]);
                    window.location.reload();
                }
            }),
        );

    // const refresh = useCallback(async () => {
    //     await delay(1500);
    //     await analyticsStore.refreshTaskReportsList(deviceIds || []);
    // }, []);

    // useEffect(() => {
    //     fork(refresh());
    // }, [refresh]);

    const items: IPickerItem[] = [
        {
            id: "existing",
            name: "Copy from recent",
            heading: "Recently added views",
            items: views
                .sort(
                    (a, b) =>
                        (Number(parseIsoDate(a?.createdAt || "")) -
                            Number(parseIsoDate(b?.createdAt || ""))) *
                        -1,
                )
                .filter(view => {
                    if (view.layoutType === "teleop") {
                        return false;
                    }

                    return true;
                })
                .map(view => {
                    const icon = layoutToIconsMap[view.layoutType || "observe"];
                    return makeItem(view.name, icon, async () => {
                        await createView(
                            view.name,
                            view.layoutType || "observe",
                            view,
                        );
                    });
                }),
        },
        {
            id: "library",
            name: "Library",
            items: [
                makeItem("Dashboard", "newView", async () => {
                    await createObserveView();
                }),
                ...(navigatorEnabled
                    ? [
                          makeItem("Navigator", "joystick", async () => {
                              await createNavigatorView();
                          }),
                      ]
                    : []),
                makeItem("Analytics", "numeric", async () => {
                    await createAnalyticsView();
                }),
            ],
        },
        {
            id: "analytics templates",
            name: "Analytics Templates",
            items: [...taskReportTemplates],
        },
    ];

    return (
        <Popup visible={true} onClose={onCancel} title="Create View">
            <PickerContainer data-testid="picker-container">
                <PickerTabGroup
                    selectedTab={selectedTab}
                    onClick={setSelectedTab}
                    items={items}
                    placeholder="Search views"
                    sorter={() => 0}
                />
            </PickerContainer>
        </Popup>
    );
}

const PickerContainer = styled.div`
    max-height: 50vh;
    height: 50vh;
    min-height: 28rem;
    padding-bottom: 1rem;
`;
